/* PompPakkie basket confirmation · 2026-08-28 */
(() => {
  'use strict';

  const pending = new WeakSet();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let panel;
  let title;
  let message;
  let basketLink;
  let returnButton;
  let refreshNumber = 0;

  function dismissPanel() {
    const restoreFocus = panel.contains(document.activeElement);
    panel.hidden = true;
    if (restoreFocus && returnButton && returnButton.isConnected) {
      returnButton.focus({ preventScroll: true });
    }
  }

  function preparePanel() {
    if (panel) return;
    const style = document.createElement('style');
    style.textContent = `
      .ppk-basket-panel{position:fixed;inset:auto 20px max(20px,env(safe-area-inset-bottom)) auto;z-index:1000;box-sizing:border-box;width:380px;max-width:calc(100vw - 32px);max-height:calc(100dvh - 32px);overflow:auto;padding:22px;background:#fffdf9;color:#10263b;border:1px solid #dce3e8;border-top:4px solid #f36b24;border-radius:14px;box-shadow:0 12px 44px rgba(7,17,27,.22);font-family:inherit;font-size:14px;font-weight:400;text-align:left;letter-spacing:0;line-height:1.5}
      .ppk-basket-panel[hidden]{display:none}
      .ppk-basket-panel__title{display:flex;align-items:center;gap:10px;margin:0;font-size:18px;font-weight:800;line-height:1.3;text-transform:none;letter-spacing:-.3px}
      .ppk-basket-panel__icon{display:grid;place-items:center;flex-shrink:0;width:28px;height:28px;border-radius:50%;background:#e5efe9;color:#235f40;font-size:17px}
      .ppk-basket-panel[data-error] .ppk-basket-panel__icon{background:#fff0df;color:#8b4500}
      .ppk-basket-panel__message{margin:10px 0 18px;color:#536574;overflow-wrap:anywhere;font-size:14px;line-height:1.5}
      .ppk-basket-panel__actions{display:grid;gap:8px}
      .ppk-basket-panel__view,.ppk-basket-panel__continue{box-sizing:border-box;display:flex;align-items:center;justify-content:center;min-height:46px;width:100%;padding:11px 16px;border:1px solid transparent;border-radius:7px;font:inherit;font-size:14px;font-weight:800;text-decoration:none;text-align:center;letter-spacing:0;text-transform:none;cursor:pointer}
      .ppk-basket-panel__view{background:#f36b24;color:#10263b}
      .ppk-basket-panel__view:hover{background:#ff813f;color:#10263b}
      .ppk-basket-panel__continue{background:transparent;color:#10263b;border-color:#d4dde3}
      .ppk-basket-panel__continue:hover{background:#eef3f6}
      .ppk-basket-panel a:focus-visible,.ppk-basket-panel button:focus-visible{outline:3px solid #215f8f;outline-offset:3px}
      @media(max-width:480px){.ppk-basket-panel{right:16px;bottom:max(16px,env(safe-area-inset-bottom));padding:18px;width:calc(100vw - 32px)}}
    `;
    document.head.append(style);

    panel = document.querySelector('[data-cart-toast]') || document.createElement('section');
    panel.className = 'cart-toast ppk-basket-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Basket update');
    panel.removeAttribute('aria-live');
    panel.hidden = true;

    const heading = document.createElement('div');
    heading.className = 'ppk-basket-panel__title';
    const icon = document.createElement('span');
    icon.className = 'ppk-basket-panel__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '✓';
    title = document.createElement('span');
    heading.append(icon, title);
    message = document.createElement('p');
    message.className = 'ppk-basket-panel__message';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.setAttribute('aria-atomic', 'true');

    const actions = document.createElement('div');
    actions.className = 'ppk-basket-panel__actions';
    basketLink = document.createElement('a');
    basketLink.className = 'ppk-basket-panel__view';
    basketLink.href = `${root}cart`;
    basketLink.textContent = 'View basket →';
    const dismiss = document.createElement('button');
    dismiss.className = 'ppk-basket-panel__continue';
    dismiss.type = 'button';
    dismiss.textContent = 'Continue shopping';
    dismiss.addEventListener('click', dismissPanel);
    actions.append(basketLink, dismiss);
    panel.replaceChildren(heading, message, actions);
    document.body.append(panel);
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissPanel();
      }
    });
  }

  function showPanel(button, text, isError = false, ownedFocus = false) {
    preparePanel();
    returnButton = button;
    panel.toggleAttribute('data-error', isError);
    panel.querySelector('.ppk-basket-panel__icon').textContent = isError ? '!' : '✓';
    title.textContent = isError ? 'Check your basket' : 'Added to your basket';
    panel.hidden = false;
    message.textContent = text;
    // Offer the next action without scrolling or redirecting the shopper.
    if (ownedFocus && (document.activeElement === button || document.activeElement === document.body)) {
      basketLink.focus({ preventScroll: true });
    }
  }

  async function refreshCount() {
    const requestNumber = ++refreshNumber;
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      if (requestNumber !== refreshNumber || !Number.isInteger(cart.item_count)) return;
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        const link = node.closest('a');
        if (link) link.setAttribute('aria-label', `Basket, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
      });
    } catch (error) {
      // The add succeeded. A failed badge refresh must never invite a duplicate add.
    }
  }

  async function handleBasketSubmit(event) {
    const form = event.currentTarget;
    if (event.defaultPrevented) return;
    const button = form.querySelector('button[type="submit"]');
    if (!button) return;
    event.preventDefault();
    if (pending.has(form) || button.disabled) return;
    pending.add(form);
    const ownedFocus = document.activeElement === button;
    const original = button.textContent;
    const originalBusy = form.getAttribute('aria-busy');
    const data = new FormData(form);
    button.disabled = true;
    button.textContent = 'Adding…';
    form.setAttribute('aria-busy', 'true');
    let response;
    let result;
    try {
      response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      result = await response.json().catch(() => null);
    } catch (error) {
      // A lost connection can occur after Shopify accepts the item. Do not auto-retry.
    } finally {
      pending.delete(form);
      button.disabled = false;
      button.textContent = original;
      if (originalBusy === null) form.removeAttribute('aria-busy');
      else form.setAttribute('aria-busy', originalBusy);
    }

    if (response && response.ok) {
      const item = result && (result.items ? result.items[0] : result);
      showPanel(button, item && typeof item.product_title === 'string' ? item.product_title : 'Your gear is in the basket. Ready when you are.', false, ownedFocus);
      void refreshCount();
    } else {
      const rejected = response && response.status >= 400 && response.status < 500;
      const detail = rejected && result && typeof result.description === 'string' ? result.description : '';
      showPanel(button, rejected ? (detail || 'This item could not be added. Check the quantity and try again.') : 'We could not confirm the update. Check your basket before trying again.', true, ownedFocus);
    }
  }

  function bindBasketForms(scope = document) {
    scope.querySelectorAll('form.quick-add, form.product-form').forEach((form) => {
      // Cancel the native submission at the form before Shopify's document-level
      // observer sees it. The successful AJAX add remains the tracked action.
      form.removeEventListener('submit', handleBasketSubmit);
      form.addEventListener('submit', handleBasketSubmit);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindBasketForms(), { once: true });
  } else {
    bindBasketForms();
  }
  document.addEventListener('shopify:section:load', (event) => bindBasketForms(event.target));
  preparePanel();
})();
